import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Assessment, InfoOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { costsApi, projectsApi } from '../../services/benchmarkApi';
import { useBenchmarkGridView } from './useBenchmarkGridView';
import { BenchmarkGridToolbar } from './BenchmarkGridToolbar';
import { formatMoney } from './benchmarkFormat';

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  margin: theme.spacing(1),
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
}));

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F8F8F8',
    color: '#6B0F2A',
    fontWeight: 600,
  },
}));

const money = (value) => formatMoney(value);

/** Nested structure-code breakdown for one project (boss drill-down). */
const STRUCTURE_TYPES = ['MASTERFORMAT', 'CBS', 'WBS', 'UNIFORMAT', 'POMI'];

function StructureDetailPanel({ projectId, structureType }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await costsApi.structureCost(projectId, structureType);
        const structureRows = res.data?.structureRows || [];
        if (cancelled) return;
        setRows(
          structureRows.map((r) => ({
            id: r.structureNodeId || r.structureCode,
            budgetCode: r.structureCode,
            projectName: r.structureName || r.structureCode,
            budgetAmount: r.originalCost,
            changeOrders: r.changeOrders,
            revisedBudget: r.revisedCost,
            actualAmount: r.actualCost,
            variance: r.variance,
            variancePercent: r.variancePercent,
          }))
        );
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load structure costs');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, structureType]);

  const columns = useMemo(
    () => [
      { field: 'budgetCode', headerName: 'Structure / Budget Code', width: 160 },
      { field: 'projectName', headerName: 'Name', flex: 1, minWidth: 140 },
      {
        field: 'budgetAmount',
        headerName: 'Estimate / BoQ',
        width: 150,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'changeOrders',
        headerName: 'Change Orders',
        width: 130,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'revisedBudget',
        headerName: 'Revised Budget',
        width: 140,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'actualAmount',
        headerName: 'Actual Cost',
        width: 130,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'variance',
        headerName: 'Variance',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'variancePercent',
        headerName: 'Variance %',
        width: 110,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => `${Number(value || 0).toFixed(2)}%`,
      },
    ],
    []
  );

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="warning" sx={{ m: 1 }}>
        {error}
      </Alert>
    );
  }
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No {structureType} cost breakdown for this project yet. Upload Estimate / Actual / CO with
        that structure type mapped, or pick another structure type in the header.
      </Typography>
    );
  }

  return (
    <Box sx={{ height: 360, width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        sx={{ border: 'none' }}
      />
    </Box>
  );
}

const BenchmarkMain = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailProjectId, setDetailProjectId] = useState(null);
  const [detailProjectLabel, setDetailProjectLabel] = useState('');
  const [detailStructureType, setDetailStructureType] = useState('MASTERFORMAT');

  const openDetail = useCallback((row) => {
    if (!row?.id && row?.id !== 0) return;
    setDetailProjectId(row.id);
    setDetailProjectLabel(row.projectName || row.budgetCode || '');
    setDetailStructureType('MASTERFORMAT');
    setDetailOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailProjectId(null);
    setDetailProjectLabel('');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const projectsRes = await projectsApi.list();
      const projects = projectsRes.data || [];
      const summaries = await Promise.all(
        projects.map(async (p) => {
          const id = p.id || p.projectId;
          try {
            const costRes = await costsApi.projectSummary(id);
            const c = costRes.data || {};
            return {
              id,
              budgetCode: p.projectNumber || p.projectCode,
              projectName: p.projectName,
              budgetAmount: c.originalCost,
              changeOrders: c.approvedChangeOrders,
              revisedBudget: c.revisedBudget,
              actualAmount: c.actualCost,
              variance: c.costVariance,
              variancePercent: c.costVariancePercent,
            };
          } catch {
            return {
              id,
              budgetCode: p.projectNumber || p.projectCode,
              projectName: p.projectName,
              budgetAmount: 0,
              changeOrders: 0,
              revisedBudget: 0,
              actualAmount: 0,
              variance: 0,
              variancePercent: 0,
            };
          }
        })
      );
      setRows(summaries);
    } catch (e) {
      setError(e.message || 'Failed to load benchmark costs');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const metricColumns = useMemo(
    () => [
      { field: 'budgetCode', headerName: 'Project Number', width: 150 },
      { field: 'projectName', headerName: 'Project Name', flex: 1, minWidth: 180 },
      {
        field: 'budgetAmount',
        headerName: 'Estimate / BoQ',
        width: 160,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'changeOrders',
        headerName: 'Change Orders',
        width: 140,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'revisedBudget',
        headerName: 'Revised Budget',
        width: 150,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'actualAmount',
        headerName: 'Actual Cost',
        width: 140,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
      },
      {
        field: 'variance',
        headerName: 'Variance',
        width: 130,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => money(value),
        cellClassName: (params) =>
          Number(params.value) < 0
            ? 'negative-variance'
            : Number(params.value) > 0
              ? 'positive-variance'
              : '',
      },
      {
        field: 'variancePercent',
        headerName: 'Variance %',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => `${Number(value || 0).toFixed(2)}%`,
        cellClassName: (params) =>
          Number(params.value) < 0
            ? 'negative-variance'
            : Number(params.value) > 0
              ? 'positive-variance'
              : '',
      },
    ],
    []
  );

  const {
    columns: viewColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    quickFilter,
    setQuickFilter,
    filterModel,
    onFilterModelChange,
    openCustomize,
    CustomizeDialog,
  } = useBenchmarkGridView('benchmark_main', metricColumns);

  const columns = useMemo(
    () => [
      ...viewColumns,
      {
        field: '_detail',
        headerName: 'Details',
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Tooltip title="View structure / budget-code details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openDetail(params.row);
              }}
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [viewColumns, openDetail]
  );

  return (
    <RootDiv>
      <ContentDiv>
        <StyledPaper>
          <TitleBar>
            <Title variant="h6">
              <Assessment fontSize="small" />
              Cost Benchmark — Estimate / BoQ vs Actual
            </Title>
            <Typography variant="caption" color="text.secondary">
              Use Details (info) on the right — or click a row — for structure / budget-code breakdown
            </Typography>
          </TitleBar>
          {error && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeItems: 'center',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            <StyledDataGrid
              rows={rows}
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              filterMode="client"
              slots={{ toolbar: BenchmarkGridToolbar }}
              slotProps={{
                toolbar: {
                  quickFilterValue: quickFilter,
                  onQuickFilterChange: setQuickFilter,
                  onOpenCustomize: openCustomize,
                },
              }}
              filterModel={filterModel}
              onFilterModelChange={onFilterModelChange}
              onRowClick={(params) => openDetail(params.row)}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              density="compact"
              sx={{
                height: '100%',
                cursor: 'pointer',
                '& .negative-variance': { color: '#d32f2f', fontWeight: 500 },
                '& .positive-variance': { color: '#2e7d32', fontWeight: 500 },
              }}
            />
          </Box>
        </StyledPaper>
      </ContentDiv>

      <Dialog
        open={detailOpen}
        onClose={closeDetail}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            minHeight: 520,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.125rem',
            pb: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            Structure / budget-code details
            {detailProjectLabel ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
                {detailProjectLabel}
              </Typography>
            ) : null}
          </Box>
          <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0, mt: 0.25 }}>
            <InputLabel>Structure type</InputLabel>
            <Select
              label="Structure type"
              value={detailStructureType}
              onChange={(e) => setDetailStructureType(e.target.value)}
            >
              {STRUCTURE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          {detailProjectId != null && (
            <StructureDetailPanel
              projectId={detailProjectId}
              structureType={detailStructureType}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            variant="contained"
            onClick={closeDetail}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {CustomizeDialog}
    </RootDiv>
  );
};

export default BenchmarkMain;
